import React, { useState } from 'react';
import { Alert, Button, Grid, IconButton, Snackbar, Tooltip } from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type GrowFormLinkProps = {
  formUrl?: string;
};

/**
 * The merchant's personal link to Grow's form. Opened from a real click rather than
 * automatically after registering, because a popup opened across an await gets blocked -- and
 * because a visible, repeatable link is what a merchant who closed the tab actually needs.
 */
export const GrowFormLink = ({ formUrl }: GrowFormLinkProps) => {
  const { t } = useTranslation();
  const [isCopiedOpen, setIsCopiedOpen] = useState(false);

  if (!formUrl) {
    return <Alert severity="info">{t('growConnect.pending.noFormLink')}</Alert>;
  }

  const onCopy = () => {
    navigator.clipboard
      .writeText(formUrl)
      .then(() => setIsCopiedOpen(true))
      .catch(() => {});
  };

  return (
    <Grid container alignItems="center" justifyContent="center" gap={1}>
      <Button
        variant="contained"
        startIcon={<OpenInNew />}
        onClick={() => window.open(formUrl, '_blank', 'noopener,noreferrer')}>
        {t('growConnect.pending.openFormCta')}
      </Button>
      <Tooltip title={t('growConnect.pending.copyFormLink')}>
        <IconButton size="small" onClick={onCopy}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <Snackbar
        open={isCopiedOpen}
        autoHideDuration={3000}
        onClose={() => setIsCopiedOpen(false)}
        message={t('growConnect.pending.formLinkCopied')}
      />
    </Grid>
  );
};
